import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import { usePromoPopupContext } from '../context/PromoPopupContext';
import { useCountryPricing } from '../hooks/useCountryPricing';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX,
  Maximize, Minimize, ChevronLeft, ChevronRight, BookmarkPlus, BookmarkCheck,
  Clock, CheckCircle, MessageSquare, FileText, Send, ThumbsUp,
  Settings, Subtitles, RotateCcw, RotateCw, Gauge, Check, Lock, Loader2
} from 'lucide-react';

import { safeArray } from '../lib/utils';
import SafeHTML from '../components/SafeHTML';
import SEO from '../components/SEO';
import {
  loadYouTubeIframeApi, extractYouTubeId, youtubeThumbnailUrl,
  YT_QUALITY_LEVELS, ytQualityLabel
} from '../lib/youtubeApi';
import { isSlowConnection, autoLocalQualityForConnection } from '../lib/networkQuality';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Render a split-view block: 55% text | 45% sticky image
const SplitViewBlock = ({ block }) => (
  <>
    <style>{`
      .split-view-block {
        display: flex;
        gap: 1.5rem;
        align-items: flex-start;
        margin-bottom: 1.5rem;
      }
      .split-text { flex: 55; min-width: 0; }
      .split-image {
        flex: 45;
        position: sticky;
        top: 80px;
        align-self: flex-start;
      }
      .split-image img { width: 100%; height: auto; border-radius: 8px; }
      .split-image .caption {
        font-size: 0.8rem;
        color: #6b7280;
        text-align: center;
        margin-top: 0.5rem;
      }
      @media (max-width: 768px) {
        .split-view-block { flex-direction: column; }
        .split-image { position: static; order: -1; width: 100%; }
      }
    `}</style>
    <div className="split-view-block">
      <div className="split-text">
        <SafeHTML html={block.content} className="text-muted-foreground leading-relaxed" />
      </div>
      {block.image_url && (
        <div className="split-image">
          <img src={`${BACKEND_URL}${block.image_url}`} alt={block.image_caption || ''} loading="lazy" />
          {block.image_caption && <p className="caption">{block.image_caption}</p>}
        </div>
      )}
    </div>
  </>
);

// Render lesson content — supports legacy string and new block array
const renderLessonContent = (description) => {
  if (!description) return null;
  if (typeof description === 'string') {
    return <SafeHTML html={description} className="text-muted-foreground leading-relaxed" />;
  }
  if (Array.isArray(description)) {
    return description.map((block) =>
      block.type === 'split_view'
        ? <SplitViewBlock key={block.id || block.content} block={block} />
        : <SafeHTML key={block.id || block.content} html={block.content} className="text-muted-foreground leading-relaxed" />
    );
  }
  return null;
};

import { getApiBaseUrl } from '@/lib/apiBase';
import { buildVideoSrc } from '@/lib/videoSrc';
import LegalAcceptCheckbox, { recordLegalAcceptance } from '../components/LegalAcceptCheckbox';
const API_URL = getApiBaseUrl();

// Speed options for video playback
const SPEED_OPTIONS = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2];

const LessonPlayer = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { token, user } = useAuth();
  const { t, tFormat } = useTranslation();
  const { triggerOnLessonComplete } = usePromoPopupContext();
  const [lesson, setLesson] = useState(null);
  const [course, setCourse] = useState(null);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);

  // View-limit access status (anti-account-sharing)
  const [accessStatus, setAccessStatus] = useState(null);
  const [premiumRequired, setPremiumRequired] = useState(false);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockTermsAccepted, setUnlockTermsAccepted] = useState(false);
  const [unlockTermsError, setUnlockTermsError] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [progress, setProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [completedAt, setCompletedAt] = useState(null);
  const [markingComplete, setMarkingComplete] = useState(false);
  const [showCompletedBanner, setShowCompletedBanner] = useState(false);
  // Admin setting (Settings → Lesson View Limits). Off by default: completed
  // lessons stay freely re-watchable. On: the "purchase re-access" overlay
  // covers a completed lesson's content. Starts false so nothing flashes locked.
  const [lockOnComplete, setLockOnComplete] = useState(false);
  const autoAdvanceTimeoutRef = useRef(null);
  
  // Quiz state
  const [quiz, setQuiz] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const quizSectionRef = useRef(null);
  
  // Video player state
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(false);
  const [subtitles, setSubtitles] = useState([]);
  const controlsTimeoutRef = useRef(null);

  // YouTube lite-embed + quality state — iframe/player is only created once
  // the user clicks play (perf win for pages that would otherwise eager-load it)
  const [ytStarted, setYtStarted] = useState(false);
  const [ytQuality, setYtQuality] = useState('default');
  const ytPlayerRef = useRef(null);

  // Local (self-hosted) video quality state
  const [videoQuality, setVideoQuality] = useState('auto'); // 'auto' | '360' | '480' | '720' | 'original'
  const [resolvedAutoQuality, setResolvedAutoQuality] = useState('original');
  const [qualitySwitching, setQualitySwitching] = useState(false);
  const pendingResumeRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };
  const { countryData } = useCountryPricing();
  const countryCode = countryData?.country_code;

  useEffect(() => {
    if (!token) {
      navigate('/login', { state: { from: { pathname: `/lessons/${lessonId}` } } });
      return;
    }
    setShowCompletedBanner(false);
    if (autoAdvanceTimeoutRef.current) clearTimeout(autoAdvanceTimeoutRef.current);
    fetchLesson();
  }, [lessonId, token]);

  // Cancel any pending auto-advance navigation if the player unmounts first
  useEffect(() => {
    return () => {
      if (autoAdvanceTimeoutRef.current) clearTimeout(autoAdvanceTimeoutRef.current);
    };
  }, []);

  // After returning from a successful re-unlock purchase, refresh access + lesson
  useEffect(() => {
    if (searchParams.get('unlock') === 'success' && token) {
      toast.success(t('lesson_unlock_success', 'Lesson re-unlocked! Enjoy.'));
      searchParams.delete('unlock');
      setSearchParams(searchParams, { replace: true });
      fetchLesson();
    }
  }, [searchParams, token]);

  // Hide controls after 3 seconds of inactivity
  useEffect(() => {
    const hideControls = () => {
      if (isPlaying && !showControls) return;
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
      controlsTimeoutRef.current = setTimeout(() => {
        if (isPlaying) setShowControls(false);
      }, 3000);
    };
    hideControls();
    return () => {
      if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
    };
  }, [isPlaying, showControls]);

  // Fullscreen change handler
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Reset per-lesson video/quality state whenever the lesson changes
  useEffect(() => {
    setYtStarted(false);
    setYtQuality('default');
    ytPlayerRef.current = null;
    setVideoQuality('auto');
    setQualitySwitching(false);
    pendingResumeRef.current = null;
  }, [lessonId]);

  // Pick a default quality for local-video "Auto" mode from the Network
  // Information API, falling back through the closest available variant
  useEffect(() => {
    const variants = lesson?.video_variants;
    if (!variants) return;
    const target = autoLocalQualityForConnection();
    const chain = target === 720 ? [720, 480, 360] : target === 360 ? [360, 480, 720] : [480, 360, 720];
    const found = chain.find((q) => variants[String(q)]);
    setResolvedAutoQuality(found ? String(found) : 'original');
  }, [lesson?.video_variants]);

  // Create the YouTube iframe player only once the user clicks play
  // (lite-youtube-embed pattern) — loads the IFrame API on demand.
  useEffect(() => {
    const youtubeId = extractYouTubeId(lesson?.youtube_url);
    if (!ytStarted || !youtubeId) return;
    let cancelled = false;
    let player = null;

    loadYouTubeIframeApi().then((YT) => {
      if (cancelled) return;
      player = new YT.Player(`yt-player-${lessonId}`, {
        videoId: youtubeId,
        playerVars: { autoplay: 1, rel: 0, modestbranding: 1 },
        events: {
          onReady: (e) => {
            ytPlayerRef.current = e.target;
            if (isSlowConnection()) {
              e.target.setPlaybackQuality('medium');
              setYtQuality('medium');
              toast.info(t('lesson_yt_slow_network', 'Switched to lower quality for your connection'));
            }
          },
          onPlaybackQualityChange: (e) => setYtQuality(e.data),
        },
      });
    });

    return () => {
      cancelled = true;
      if (player && player.destroy) player.destroy();
      ytPlayerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ytStarted, lessonId]);

  const changeYoutubeQuality = (ytValue) => {
    if (!ytPlayerRef.current) return;
    ytPlayerRef.current.setPlaybackQuality(ytValue);
    setYtQuality(ytValue);
  };

  const getLocalVideoUrl = () => {
    if (!lesson) return '';
    const variants = lesson.video_variants;
    if (!variants) return lesson.video_url;
    const effective = videoQuality === 'auto' ? resolvedAutoQuality : videoQuality;
    if (effective === 'original') return variants.original || lesson.video_url;
    return variants[effective] || variants.original || lesson.video_url;
  };

  const changeVideoQuality = (newQuality) => {
    if (newQuality === videoQuality) return;
    if (videoRef.current) {
      pendingResumeRef.current = { time: videoRef.current.currentTime, playing: !videoRef.current.paused };
    }
    setQualitySwitching(true);
    setVideoQuality(newQuality);
  };

  const fetchAccessStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/lessons/${lessonId}/access-status`, {
        headers,
        params: countryCode ? { country_code: countryCode } : undefined,
      });
      setAccessStatus(res.data);
      return res.data;
    } catch (err) {
      return null;
    }
  };

  const fetchLesson = async () => {
    try {
      setLoading(true);
      setPremiumRequired(false);

      const access = await fetchAccessStatus();
      if (access && access.enabled && !access.allowed) {
        setLesson(null);
        setLoading(false);
        return;
      }

      const lessonRes = await axios.get(`${API_URL}/lessons/${lessonId}`, { headers });
      setLesson(lessonRes.data);
      setIsCompleted(!!lessonRes.data.is_completed);
      setCompletedAt(lessonRes.data.completed_at || null);

      // Fetch subtitles if available
      if (lessonRes.data.subtitles_url) {
        try {
          const subtitlesRes = await axios.get(lessonRes.data.subtitles_url);
          setSubtitles(parseVTT(subtitlesRes.data));
        } catch (e) {}
      }

      // Store course_id from lesson for back navigation fallback
      const courseId = lessonRes.data?.course_id;
      
      const [courseRes, lessonsRes, commentsRes] = await Promise.all([
        axios.get(`${API_URL}/courses/${courseId}`).catch(() => ({ data: null })),
        axios.get(`${API_URL}/courses/${courseId}/lessons`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/comments/${lessonId}`).catch(() => ({ data: [] }))
      ]);

      setCourse(courseRes.data ? { ...courseRes.data, id: courseRes.data.id || courseId } : { id: courseId });
      setLessons(Array.isArray(lessonsRes.data) ? lessonsRes.data : []);
      setComments(Array.isArray(commentsRes.data) ? commentsRes.data : []);

      // Fetch quiz for this lesson
      try {
        const quizRes = await axios.get(`${API_URL}/quiz/${lessonId}`, { headers });
        console.log('Quiz data:', quizRes.data);
        if (quizRes.data) {
          setQuiz(quizRes.data);
          setQuizAnswers(new Array(safeArray(quizRes.data.questions).length).fill(-1));

          // Hydrate the latest past attempt so the completion gate reflects
          // quizzes already passed in a previous session, not just this one.
          try {
            const resultsRes = await axios.get(`${API_URL}/quiz/${lessonId}/results`, { headers });
            const latest = safeArray(resultsRes.data?.results)[0];
            if (latest) {
              setQuizResult(latest);
              setQuizSubmitted(true);
            }
          } catch (err) {}
        }
      } catch (err) {
        console.log('No quiz for this lesson');
      }

      // Check bookmark status
      try {
        const bookmarksRes = await axios.get(`${API_URL}/bookmarks`, { headers });
        setIsBookmarked(safeArray(bookmarksRes.data).some((b) => b?.id === lessonId));
      } catch (err) {}
    } catch (err) {
      console.error(err);
      if (err.response?.status === 403) {
        const detail = err.response?.data?.detail;
        if (detail && typeof detail === 'object' && detail.reason === 'view_limit_reached') {
          setAccessStatus((prev) => ({ ...(prev || {}), enabled: true, allowed: false, ...detail }));
          setLesson(null);
        } else if (detail && typeof detail === 'object' && detail.reason === 'premium_required') {
          setLesson(null);
          setPremiumRequired(true);
        } else {
          toast.error(t('lesson_premium_required', 'Premium access required for this lesson'));
          navigate('/pricing');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Parse VTT subtitle format
  const parseVTT = (vttContent) => {
    const lines = String(vttContent ?? '').split('\n');
    const cues = [];
    let i = 0;
    while (i < lines.length) {
      if (lines[i].includes('-->')) {
        const [start, end] = lines[i].split('-->').map(t => parseTime(t.trim()));
        let text = '';
        i++;
        while (i < lines.length && lines[i].trim()) {
          text += lines[i] + '\n';
          i++;
        }
        cues.push({ start, end, text: text.trim() });
      }
      i++;
    }
    return cues;
  };

  const parseTime = (timeStr) => {
    const parts = timeStr.split(':');
    if (parts.length === 3) {
      const [h, m, s] = parts;
      return parseFloat(h) * 3600 + parseFloat(m) * 60 + parseFloat(s.replace(',', '.'));
    } else if (parts.length === 2) {
      const [m, s] = parts;
      return parseFloat(m) * 60 + parseFloat(s.replace(',', '.'));
    }
    return 0;
  };

  const getCurrentSubtitle = () => {
    if (!subtitlesEnabled || subtitles.length === 0) return null;
    const cue = subtitles.find(s => currentTime >= s.start && currentTime <= s.end);
    return cue?.text || null;
  };

  // Video controls
  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (value) => {
    if (!videoRef.current) return;
    const time = (value[0] / 100) * duration;
    videoRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const skipTime = (seconds) => {
    if (!videoRef.current) return;
    videoRef.current.currentTime = Math.max(0, Math.min(duration, currentTime + seconds));
  };

  const handleVolumeChange = (value) => {
    if (!videoRef.current) return;
    const vol = value[0] / 100;
    videoRef.current.volume = vol;
    setVolume(vol);
    setIsMuted(vol === 0);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const changeSpeed = (speed) => {
    if (!videoRef.current) return;
    videoRef.current.playbackRate = speed;
    setPlaybackSpeed(speed);
    toast.success(tFormat('lesson_speed_toast', { speed }, `Playback speed: ${speed}x`));
  };

  const toggleFullscreen = async () => {
    if (!videoContainerRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await videoContainerRef.current.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleBookmark = async () => {
    try {
      const res = await axios.post(`${API_URL}/bookmarks/${lessonId}`, {}, { headers });
      setIsBookmarked(res.data.bookmarked);
      toast.success(
        res.data.bookmarked
          ? t('lesson_bookmarked_toast', 'Lesson bookmarked')
          : t('lesson_unbookmarked_toast', 'Bookmark removed')
      );
    } catch (err) {
      toast.error(t('lesson_bookmark_fail', 'Failed to update bookmark'));
    }
  };

  const handleProgress = async () => {
    try {
      await axios.post(`${API_URL}/progress`, {
        lesson_id: lessonId,
        watch_time: Math.round(progress * (lesson?.duration_minutes || 0) * 60),
        progress_percent: progress * 100,
        completed: false
      }, { headers });
    } catch (err) {}
  };

  // A lesson with a quiz can't be marked complete until it's been taken
  // (and, if graded, passed) — mirrors the backend gate in
  // backend/routes/progress.py so the button reflects it before the API call.
  const quizRequired = !!quiz && safeArray(quiz.questions).length > 0;
  const quizPassed = !quizRequired || (quizResult != null && (quiz.is_graded === false || quizResult.passed === true));
  const quizGateBlocked = quizRequired && !isCompleted && !quizPassed;

  const scrollToQuiz = () => {
    quizSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    axios.get(`${API_URL}/settings`)
      .then((res) => setLockOnComplete(res.data?.lock_lessons_on_completion === true))
      .catch(() => {});
  }, []);

  const completionLocked = isCompleted && lockOnComplete;

  // Lessons only complete on an explicit user click — never auto-completed
  // by video playback — since completion can lock the lesson behind the
  // view-limit/re-access system (see backend/utils/view_limits.py).
  const markLessonComplete = async () => {
    if (isCompleted || markingComplete) return;
    setMarkingComplete(true);
    try {
      const res = await axios.post(`${API_URL}/progress`, {
        lesson_id: lessonId,
        watch_time: Math.round(progress * (lesson?.duration_minutes || 0) * 60),
        progress_percent: 100,
        completed: true
      }, { headers });
      setIsCompleted(true);
      setCompletedAt(res.data?.completed_at || new Date().toISOString());
      toast.success(t('lesson_completed_toast', 'Lesson completed! +10 points'));
      triggerOnLessonComplete?.();

      // Auto-advance: next lesson in this course, else next course, else dashboard.
      // The banner stays informational only — dismissing it doesn't cancel the
      // scheduled navigation, the manual buttons inside it do that immediately instead.
      setShowCompletedBanner(true);
      const nav = lessonNav;
      if (nextLesson) {
        autoAdvanceTimeoutRef.current = setTimeout(() => {
          navigate(`/lessons/${nextLesson.id}`);
        }, 2000);
      } else if (nav.next_course) {
        autoAdvanceTimeoutRef.current = setTimeout(() => {
          navigate(nav.next_course.first_lesson_id
            ? `/lessons/${nav.next_course.first_lesson_id}`
            : `/courses/${nav.next_course.id}`);
        }, 3000);
      } else if (nav.is_last_in_course) {
        autoAdvanceTimeoutRef.current = setTimeout(() => {
          navigate('/dashboard?completed=all');
        }, 3000);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && (detail.reason === 'quiz_required' || detail.reason === 'quiz_not_passed')) {
        toast.error(detail.message || t('lesson_quiz_gate_toast', 'Complete the quiz before marking this lesson as complete.'));
        scrollToQuiz();
      } else {
        toast.error(t('lesson_mark_complete_fail', 'Could not mark lesson as complete. Please try again.'));
      }
    } finally {
      setMarkingComplete(false);
    }
  };

  // Entry point for both "Mark as Complete" buttons — blocks locally when the
  // quiz gate applies (scrolling the user to it) instead of hitting the API.
  const handleMarkComplete = () => {
    if (isCompleted || markingComplete) return;
    if (quizGateBlocked) {
      toast.error(t('lesson_quiz_gate_toast', 'Complete the quiz before marking this lesson as complete.'));
      scrollToQuiz();
      return;
    }
    markLessonComplete();
  };

  const handleUnlock = async (paymentMethod) => {
    if (!unlockTermsAccepted) {
      setUnlockTermsError(true);
      return;
    }
    setUnlocking(true);
    try {
      await recordLegalAcceptance(headers, 'reaccess');
      if (paymentMethod === 'stripe') {
        const res = await axios.post(`${API_URL}/payments/stripe/reaccess-checkout`, {
          lesson_id: lessonId,
          country_code: countryCode
        }, {
          headers,
          params: { origin_url: window.location.origin }
        });
        if (res.data?.url) window.location.href = res.data.url;
      } else {
        const res = await axios.post(`${API_URL}/payments/paypal/reaccess-checkout`, {
          lesson_id: lessonId,
          country_code: countryCode,
          origin_url: window.location.origin
        }, { headers });
        if (res.data?.approval_url) window.location.href = res.data.approval_url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('lesson_unlock_fail', 'Failed to start checkout'));
    } finally {
      setUnlocking(false);
    }
  };

  const handleComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    try {
      await axios.post(`${API_URL}/comments`, {
        lesson_id: lessonId,
        content: newComment
      }, { headers });
      
      setNewComment('');
      const commentsRes = await axios.get(`${API_URL}/comments/${lessonId}`);
      setComments(commentsRes.data);
      toast.success(t('lesson_comment_posted', 'Comment posted'));
    } catch (err) {
      toast.error(t('lesson_comment_fail', 'Failed to post comment'));
    }
  };

  const currentIndex = lessons.findIndex(l => l.id === lessonId);
  const prevLesson = currentIndex > 0 ? lessons[currentIndex - 1] : null;
  const nextLesson = currentIndex < lessons.length - 1 ? lessons[currentIndex + 1] : null;
  const lessonNav = lesson?.navigation || {};

  const goToLesson = (id) => {
    if (progress > 0) handleProgress();
    navigate(`/lessons/${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">
          {t('lesson_loading', 'Loading lesson...')}
        </div>
      </div>
    );
  }

  if (!lesson) {
    if (accessStatus?.enabled && accessStatus?.allowed === false) {
      const reaccessPackage = accessStatus.reaccess_package;
      const packageName = reaccessPackage?.name || t('lesson_reaccess_pkg_default_name', 'Study Again Pass');
      const price = reaccessPackage?.price ?? accessStatus.reattempt_price;
      // Country-aware currency symbol (see access-status's `currency_symbol`,
      // set when the user's country has its own re-access price override) —
      // defaults to ₹ so India's display is byte-identical to before this.
      const currencySymbol = reaccessPackage?.currency_symbol || accessStatus.currency_symbol || '₹';
      const formattedPrice = `${currencySymbol}${price}`;
      return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="lesson-locked">
          <Card className="max-w-md w-full">
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center">
                <Lock className="w-8 h-8 text-muted-foreground" />
              </div>
              {accessStatus.lesson_title && (
                <h2 className="text-xl font-semibold" data-testid="locked-lesson-title">{accessStatus.lesson_title}</h2>
              )}
              <p className="text-muted-foreground">
                {t('lesson_completed_locked_desc', 'You have completed this lesson')}
              </p>
              <div className="rounded-lg border p-4" data-testid="reaccess-package-info">
                <p className="font-medium">{packageName}</p>
                <p className="text-sm text-muted-foreground">
                  {tFormat('lesson_view_limit_count', { count: accessStatus.view_count, max: accessStatus.max_views }, 'Viewed {count} of {max} times')}
                </p>
              </div>
              <Button onClick={() => setUnlockDialogOpen(true)} className="w-full" data-testid="study-again-btn">
                {tFormat('lesson_unlock_study_again_amount', { amount: formattedPrice }, 'Unlock to Study Again — {amount}')}
              </Button>
              <Button variant="outline" onClick={() => navigate('/courses')} className="w-full">
                {t('course_back_catalog', 'Back to Courses')}
              </Button>
            </CardContent>
          </Card>

          <Dialog open={unlockDialogOpen} onOpenChange={setUnlockDialogOpen}>
            <DialogContent className="max-w-sm">
              <DialogHeader>
                <DialogTitle>{tFormat('lesson_unlock_study_again_amount', { amount: formattedPrice }, 'Unlock to Study Again — {amount}')}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground">
                {t('lesson_unlock_dialog_desc', 'Choose a payment method to unlock this lesson again.')}
              </p>
              <LegalAcceptCheckbox
                checked={unlockTermsAccepted}
                onChange={(v) => {
                  setUnlockTermsAccepted(v);
                  if (v) setUnlockTermsError(false);
                }}
                error={unlockTermsError}
                purchase={false}
                testId="reaccess-accept-terms"
              />
              <DialogFooter className="flex-col gap-2 sm:flex-col">
                <Button onClick={() => handleUnlock('stripe')} disabled={unlocking} className="w-full">
                  {t('lesson_pay_stripe', 'Pay with Card (Stripe)')}
                </Button>
                <Button onClick={() => handleUnlock('paypal')} disabled={unlocking} variant="outline" className="w-full">
                  {t('lesson_pay_paypal', 'Pay with PayPal')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      );
    }

    if (premiumRequired) {
      return (
        <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="lesson-premium-locked">
          <Card className="max-w-md w-full">
            <CardContent className="p-8 text-center space-y-4">
              <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center">
                <Lock className="w-8 h-8 text-muted-foreground" />
              </div>
              <h2 className="text-xl font-semibold">
                {t('lesson_premium_locked_title', 'This is premium content')}
              </h2>
              <p className="text-muted-foreground">
                {t('lesson_premium_locked_desc', 'Upgrade your plan to unlock this lesson and the rest of the course.')}
              </p>
              <Button onClick={() => navigate('/pricing')} className="w-full" data-testid="get-access-btn">
                {t('lesson_get_access_cta', 'Get Access')}
              </Button>
              <Button variant="outline" onClick={() => navigate('/courses')} className="w-full">
                {t('course_back_catalog', 'Back to Courses')}
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-4">{t('lesson_not_found', 'Lesson not found')}</h2>
          <Button onClick={() => navigate('/courses')}>
            {t('course_back_catalog', 'Back to Courses')}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="lesson-player">
      <SEO
        title={`${lesson.title} — ${course?.title || 'Interior Training'}`}
        description={`Learn ${lesson.title}. Part of ${course?.title || 'the'} course. Interior technician and carpentry training online.`}
        keywords={`${lesson.title}, interior technician training, ${course?.title || ''}`}
        url={`/lessons/${lesson.id}`}
        noIndex={true}
      />

      {showCompletedBanner && (
        <div
          className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between gap-4 px-6 py-4 text-white shadow-lg animate-in slide-in-from-top duration-300"
          style={{ background: 'linear-gradient(135deg, #15803d, #16a34a)' }}
          data-testid="lesson-complete-banner"
        >
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-2xl">🎉</span>
            <div className="min-w-0">
              <p className="font-bold text-sm">{t('lesson_banner_complete', 'Lesson Complete!')}</p>
              <p className="text-xs opacity-90 truncate">
                {nextLesson
                  ? t('lesson_banner_next', 'Moving to: {{title}}...').replace('{{title}}', nextLesson.title)
                  : lessonNav.next_course
                  ? t('lesson_banner_next_course', 'Course complete! Moving to: {{title}}...').replace('{{title}}', lessonNav.next_course.title)
                  : t('lesson_banner_all_done', 'All courses complete! Amazing work! 🏆')}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {nextLesson && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => goToLesson(nextLesson.id)}
                data-testid="banner-next-lesson-btn"
              >
                {t('lesson_banner_next_btn', 'Next Lesson')} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            {!nextLesson && lessonNav.next_course && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => lessonNav.next_course.first_lesson_id
                  ? goToLesson(lessonNav.next_course.first_lesson_id)
                  : navigate(`/courses/${lessonNav.next_course.id}`)}
                data-testid="banner-next-course-btn"
              >
                {t('lesson_banner_next_course_btn', 'Next Course')} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
            <button
              onClick={() => setShowCompletedBanner(false)}
              className="text-white/70 hover:text-white text-xl leading-none px-1"
              aria-label="Dismiss"
              data-testid="banner-dismiss-btn"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Top Navigation */}
      <div className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/courses/${course?.id || lesson?.course_id}`)}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <p className="text-sm text-muted-foreground">{course?.title}</p>
              <h1 className="font-semibold truncate max-w-md">{lesson.title}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {accessStatus?.enabled && !accessStatus?.unlimited && (
              <Badge variant="outline" data-testid="view-count-badge">
                {tFormat('lesson_view_count_badge', { count: accessStatus.view_count, max: accessStatus.max_views }, 'Viewing {count} of {max}')}
              </Badge>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={handleBookmark}
              data-testid="bookmark-btn"
            >
              {isBookmarked ? (
                <BookmarkCheck className="w-5 h-5 text-accent" />
              ) : (
                <BookmarkPlus className="w-5 h-5" />
              )}
            </Button>
            <Button
              variant={isCompleted ? 'secondary' : 'outline'}
              size="sm"
              onClick={handleMarkComplete}
              disabled={isCompleted || markingComplete}
              className={quizGateBlocked ? 'text-muted-foreground' : undefined}
              data-testid="mark-complete-btn"
            >
              {isCompleted ? (
                <>
                  <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                  {t('lesson_completed_badge', 'Completed')}
                </>
              ) : markingComplete ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('lesson_marking', 'Marking...')}
                </>
              ) : quizGateBlocked ? (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  {t('lesson_complete_quiz_first', 'Complete Quiz First')}
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  {t('lesson_mark_complete', 'Mark Complete')}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Video Player */}
          <div className="lg:col-span-2 space-y-6">
            {/* Video Container — admin can disable per-course */}
            {course?.enable_video !== false && (
            <div 
              ref={videoContainerRef}
              className="relative aspect-video bg-black rounded-xl overflow-hidden group" 
              data-testid="video-container"
              onMouseMove={() => setShowControls(true)}
              onMouseLeave={() => isPlaying && setShowControls(false)}
            >
              {lesson.video_type === 'youtube' && lesson.youtube_url ? (
                (() => {
                  const youtubeId = extractYouTubeId(lesson.youtube_url);
                  if (!youtubeId) {
                    return (
                      <div className="w-full h-full flex items-center justify-center text-white/50">
                        <p>{t('lesson_video_invalid_youtube', 'Invalid YouTube URL')}</p>
                      </div>
                    );
                  }
                  if (!ytStarted) {
                    // lite-youtube-embed pattern: thumbnail + play button only,
                    // no iframe/JS loaded until the user actually clicks play.
                    return (
                      <button
                        type="button"
                        onClick={() => setYtStarted(true)}
                        className="absolute inset-0 w-full h-full group/yt"
                        data-testid="youtube-lite-play"
                      >
                        <img
                          src={youtubeThumbnailUrl(youtubeId)}
                          alt={lesson.title}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover/yt:bg-black/40 transition">
                          <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
                            <Play className="w-8 h-8 text-black ml-1" />
                          </div>
                        </div>
                      </button>
                    );
                  }
                  return (
                    <>
                      <div id={`yt-player-${lessonId}`} className="absolute inset-0 w-full h-full" />
                      {/* Quality selector + badge overlaid on the YouTube iframe.
                          Top-right (not bottom-right) so it doesn't collide with
                          YouTube's own native bottom control bar. */}
                      <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
                        <span className="bg-black/70 text-white text-xs px-2 py-1 rounded" data-testid="yt-quality-badge">
                          {ytQualityLabel(ytQuality)}
                        </span>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-white bg-black/50 hover:bg-black/70 w-8 h-8" data-testid="yt-quality-btn">
                              <Settings className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {YT_QUALITY_LEVELS.map((q) => (
                              <DropdownMenuItem
                                key={q.ytValue}
                                onClick={() => changeYoutubeQuality(q.ytValue)}
                                className={ytQuality === q.ytValue ? 'bg-accent/10' : ''}
                              >
                                {q.label} {ytQuality === q.ytValue && '✓'}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </>
                  );
                })()
              ) : lesson.video_url ? (
                <>
                  <video
                    ref={videoRef}
                    src={buildVideoSrc(getLocalVideoUrl(), token)}
                    preload="metadata"
                    className="w-full h-full object-contain"
                    onClick={togglePlay}
                    onTimeUpdate={(e) => {
                      const vid = e.target;
                      setCurrentTime(vid.currentTime);
                      setProgress(vid.currentTime / vid.duration || 0);
                    }}
                    onLoadedMetadata={(e) => {
                      setDuration(e.target.duration);
                      if (pendingResumeRef.current) {
                        const { time, playing } = pendingResumeRef.current;
                        e.target.currentTime = time;
                        if (playing) e.target.play().catch(() => {});
                        pendingResumeRef.current = null;
                      }
                      setQualitySwitching(false);
                    }}
                    onEnded={() => setIsPlaying(false)}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                  />

                  {/* Quality-switch loading indicator */}
                  {qualitySwitching && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none" data-testid="quality-switch-loading">
                      <Loader2 className="w-8 h-8 text-white animate-spin" />
                    </div>
                  )}

                  {/* Persistent quality badge, top-right corner */}
                  {lesson.video_variants && (
                    <div className="absolute top-3 right-3 z-10">
                      <span className="bg-black/70 text-white text-xs px-2 py-1 rounded" data-testid="local-quality-badge">
                        {videoQuality === 'auto'
                          ? `${t('lesson_quality_auto', 'Auto')} (${resolvedAutoQuality === 'original' ? t('lesson_quality_original', 'Original') : `${resolvedAutoQuality}p`})`
                          : (videoQuality === 'original' ? t('lesson_quality_original', 'Original') : `${videoQuality}p`)}
                      </span>
                    </div>
                  )}

                  {/* Subtitles overlay */}
                  {getCurrentSubtitle() && (
                    <div className="absolute bottom-20 left-0 right-0 text-center">
                      <span className="bg-black/80 text-white px-4 py-2 rounded text-lg">
                        {getCurrentSubtitle()}
                      </span>
                    </div>
                  )}

                  {/* Custom controls overlay */}
                  <div 
                    className={`absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent transition-opacity duration-300 ${
                      showControls ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {/* Center play button */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <button
                        onClick={togglePlay}
                        className="w-16 h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center hover:bg-white/30 transition"
                      >
                        {isPlaying ? (
                          <Pause className="w-8 h-8 text-white" />
                        ) : (
                          <Play className="w-8 h-8 text-white ml-1" />
                        )}
                      </button>
                    </div>

                    {/* Bottom controls */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 space-y-2">
                      {/* Progress bar */}
                      <div className="flex items-center gap-3">
                        <span className="text-white text-sm w-12">{formatTime(currentTime)}</span>
                        <Slider
                          value={[duration ? (currentTime / duration) * 100 : 0]}
                          onValueChange={handleSeek}
                          max={100}
                          step={0.1}
                          className="flex-1"
                          data-testid="video-progress-slider"
                        />
                        <span className="text-white text-sm w-12">{formatTime(duration)}</span>
                      </div>

                      {/* Control buttons */}
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Button variant="ghost" size="icon" onClick={togglePlay} className="text-white hover:bg-white/20">
                            {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => skipTime(-10)} className="text-white hover:bg-white/20">
                            <RotateCcw className="w-5 h-5" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => skipTime(10)} className="text-white hover:bg-white/20">
                            <RotateCw className="w-5 h-5" />
                          </Button>
                          
                          {/* Volume */}
                          <div className="flex items-center gap-2 ml-2">
                            <Button variant="ghost" size="icon" onClick={toggleMute} className="text-white hover:bg-white/20">
                              {isMuted || volume === 0 ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                            </Button>
                            <div className="w-24 hidden sm:block">
                              <Slider
                                value={[isMuted ? 0 : volume * 100]}
                                onValueChange={handleVolumeChange}
                                max={100}
                                step={1}
                                data-testid="volume-slider"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {/* Quality control (only when local-upload variants exist) */}
                          {lesson.video_variants && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 gap-1" data-testid="local-quality-control">
                                  <Settings className="w-4 h-4" />
                                  {videoQuality === 'auto'
                                    ? t('lesson_quality_auto', 'Auto')
                                    : (videoQuality === 'original' ? t('lesson_quality_original', 'Original') : `${videoQuality}p`)}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => changeVideoQuality('auto')} className={videoQuality === 'auto' ? 'bg-accent/10' : ''}>
                                  {t('lesson_quality_auto', 'Auto')} ({resolvedAutoQuality === 'original' ? t('lesson_quality_original', 'Original') : `${resolvedAutoQuality}p`}) {videoQuality === 'auto' && '✓'}
                                </DropdownMenuItem>
                                {['720', '480', '360'].filter((q) => lesson.video_variants?.[q]).map((q) => (
                                  <DropdownMenuItem key={q} onClick={() => changeVideoQuality(q)} className={videoQuality === q ? 'bg-accent/10' : ''}>
                                    {q}p {videoQuality === q && '✓'}
                                  </DropdownMenuItem>
                                ))}
                                <DropdownMenuItem onClick={() => changeVideoQuality('original')} className={videoQuality === 'original' ? 'bg-accent/10' : ''}>
                                  {t('lesson_quality_original', 'Original')} {videoQuality === 'original' && '✓'}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}

                          {/* Speed control */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-white hover:bg-white/20 gap-1" data-testid="speed-control">
                                <Gauge className="w-4 h-4" />
                                {playbackSpeed}x
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              {SPEED_OPTIONS.map((speed) => (
                                <DropdownMenuItem
                                  key={speed}
                                  onClick={() => changeSpeed(speed)}
                                  className={playbackSpeed === speed ? 'bg-accent/10' : ''}
                                >
                                  {speed}x {playbackSpeed === speed && '✓'}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>

                          {/* Subtitles toggle */}
                          {subtitles.length > 0 && (
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => setSubtitlesEnabled(!subtitlesEnabled)}
                              className={`text-white hover:bg-white/20 ${subtitlesEnabled ? 'bg-white/20' : ''}`}
                              data-testid="subtitles-toggle"
                            >
                              <Subtitles className="w-5 h-5" />
                            </Button>
                          )}

                          {/* Fullscreen */}
                          <Button variant="ghost" size="icon" onClick={toggleFullscreen} className="text-white hover:bg-white/20" data-testid="fullscreen-btn">
                            {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex items-center justify-center text-white/50">
                  <div className="text-center">
                    <Play className="w-16 h-16 mx-auto mb-4" />
                    <p>{t('lesson_video_soon', 'Video content coming soon')}</p>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between">
              <Button
                variant="outline"
                onClick={() => prevLesson && goToLesson(prevLesson.id)}
                disabled={!prevLesson}
                data-testid="prev-lesson-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                {t('lesson_previous', 'Previous')}
              </Button>
              <Button
                onClick={() => nextLesson && goToLesson(nextLesson.id)}
                disabled={!nextLesson}
                data-testid="next-lesson-btn"
              >
                {t('lesson_next_nav', 'Next')}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            {isCompleted && !lockOnComplete && (
              <div
                className="flex items-center gap-3 rounded-xl border border-green-300 dark:border-green-900/60 bg-green-50 dark:bg-green-950/20 px-4 py-3"
                data-testid="lesson-rewatch-banner"
              >
                <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                <div>
                  <p className="text-sm font-bold text-green-700 dark:text-green-400">
                    {t('lesson_rewatch_title', 'You completed this lesson')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('lesson_rewatch_desc', 'You can rewatch it anytime.')}
                  </p>
                </div>
              </div>
            )}

            {/* Tabs — locked behind a blurred overlay once the lesson is completed,
                only when the admin's lock_lessons_on_completion setting is on;
                the video above stays playable, only this content area locks. */}
            <div className="relative">
            {completionLocked && (
              <div
                className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center p-8 bg-white/85 dark:bg-black/80 backdrop-blur-sm rounded-xl"
                data-testid="lesson-locked-overlay"
              >
                <div className="text-5xl mb-3">✅</div>
                <h3 className="text-lg font-bold mb-2">
                  {t('lesson_locked_heading', 'Lesson Completed!')}
                </h3>
                <p className="text-sm text-muted-foreground mb-5 max-w-sm">
                  {t(
                    'lesson_locked_desc',
                    'This content is locked. Purchase re-access to study again.'
                  )}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Button
                    onClick={() => {
                      if (nextLesson) goToLesson(nextLesson.id);
                      else if (lessonNav.next_course?.first_lesson_id) goToLesson(lessonNav.next_course.first_lesson_id);
                      else if (lessonNav.next_course) navigate(`/courses/${lessonNav.next_course.id}`);
                      else navigate(`/courses/${course?.id}`);
                    }}
                    className="rounded-full px-6"
                    data-testid="locked-continue-btn"
                  >
                    {t('lesson_locked_continue', 'Continue to Next Lesson')} <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/courses/${course?.id}#reaccess`)}
                    className="rounded-full px-6"
                    data-testid="locked-reaccess-btn"
                  >
                    {t('lesson_locked_reaccess', 'Purchase Re-access')}
                  </Button>
                </div>
              </div>
            )}
            <div className={completionLocked ? 'pointer-events-none select-none blur-[2px]' : ''}>
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="w-full justify-start">
                <TabsTrigger value="overview">{t('lesson_tab_overview', 'Overview')}</TabsTrigger>
                {course?.enable_comments !== false && (
                  <TabsTrigger value="comments">
                    {t('lesson_tab_comments', 'Comments')} ({comments.length})
                  </TabsTrigger>
                )}
                {lesson.notes_url && (
                  <TabsTrigger value="notes">{t('lesson_tab_notes', 'Notes')}</TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="overview" className="mt-6">
                <Card>
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-serif font-medium mb-4">{lesson.title}</h2>
                    {renderLessonContent(lesson.description)}
                    <div className="flex items-center gap-4 mt-6 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {lesson.duration_minutes} {t('course_meta_min', 'min')}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="comments" className="mt-6" data-testid="comments-section">
                <Card>
                  <CardContent className="p-6">
                    <form onSubmit={handleComment} className="mb-6">
                      <Textarea
                        placeholder={t(
                          'lesson_comment_placeholder',
                          'Ask a question or share your thoughts...'
                        )}
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        className="mb-3"
                        data-testid="comment-input"
                      />
                      <Button type="submit" data-testid="submit-comment-btn">
                        <Send className="w-4 h-4 mr-2" />
                        {t('lesson_post_comment', 'Post Comment')}
                      </Button>
                    </form>

                    <div className="space-y-4">
                      {comments.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">
                          {t('lesson_comments_empty', 'No comments yet. Be the first to share!')}
                        </p>
                      ) : (
                        comments.map((comment) => (
                          <div key={comment.id} className="border-b pb-4 last:border-0">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                                <span className="text-sm font-medium text-accent">
                                  {comment.user_name?.charAt(0) || 'U'}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-sm">{comment.user_name}</p>
                                {comment.is_instructor && (
                                  <Badge variant="secondary" className="text-xs">
                                    {t('lesson_instructor', 'Instructor')}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            <p className="text-muted-foreground ml-11">{comment.content}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {lesson.notes_url && (
                <TabsContent value="notes" className="mt-6">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold">{t('lesson_notes_title', 'Lesson Notes')}</h3>
                        <Button variant="outline" asChild>
                          <a href={lesson.notes_url} target="_blank" rel="noopener noreferrer">
                            <FileText className="w-4 h-4 mr-2" />
                            {t('lesson_download_notes', 'Download Notes')}
                          </a>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              )}
            </Tabs>
            </div>
            </div>

            {/* Mark-as-complete / locked-after-complete panel */}
            <Card>
              <CardContent className="p-6 text-center" data-testid="lesson-completion-panel">
                {!isCompleted ? (
                  <>
                    <p className="text-sm text-muted-foreground mb-4">
                      {quizGateBlocked
                        ? t('lesson_complete_quiz_gate_prompt', '🔒 Complete the quiz below to unlock this lesson.')
                        : t('lesson_complete_prompt', 'Finished watching and reading this lesson?')}
                    </p>
                    <Button
                      onClick={handleMarkComplete}
                      disabled={markingComplete}
                      variant={quizGateBlocked ? 'outline' : 'default'}
                      className={`rounded-full px-8 ${quizGateBlocked ? 'text-muted-foreground' : ''}`}
                      data-testid="mark-complete-btn-inline"
                    >
                      {markingComplete ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : quizGateBlocked ? (
                        <Lock className="w-4 h-4 mr-2" />
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      {markingComplete
                        ? t('lesson_marking', 'Marking...')
                        : quizGateBlocked
                          ? t('lesson_complete_quiz_first', 'Complete Quiz First')
                          : t('lesson_mark_complete', 'Mark as Complete')}
                    </Button>
                    {lockOnComplete && (
                      <p className="text-xs text-muted-foreground mt-3">
                        {t(
                          'lesson_complete_warning',
                          'Once marked complete, re-access requires purchase.'
                        )}
                      </p>
                    )}
                  </>
                ) : (
                  <div className="rounded-xl border-2 border-green-200 dark:border-green-900/50 bg-green-50 dark:bg-green-950/20 p-6">
                    <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
                    <h3 className="font-semibold text-green-700 dark:text-green-400 mb-1">
                      {t('lesson_completed_heading', 'Lesson Completed!')}
                    </h3>
                    {completedAt && (
                      <p className="text-xs text-muted-foreground mb-2">
                        {tFormat(
                          'lesson_completed_on',
                          { date: new Date(completedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) },
                          'Completed on {date}'
                        )}
                      </p>
                    )}
                    <p className="text-sm text-muted-foreground">
                      {lockOnComplete
                        ? t(
                          'lesson_completed_reaccess_hint',
                          'To study this lesson again, purchase re-access from the course page.'
                        )
                        : t('lesson_rewatch_desc', 'You can rewatch it anytime.')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Course Outline */}
          <div className="space-y-6" data-testid="lesson-sidebar">
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-4">
                  {t('course_content_heading', 'Course Content')}
                </h3>
                <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                  {lessons.map((l, index) => (
                    <button
                      key={l.id}
                      onClick={() => goToLesson(l.id)}
                      className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-3 ${
                        l.id === lessonId 
                          ? 'bg-accent/10 text-accent' 
                          : 'hover:bg-muted'
                      }`}
                      data-testid={`sidebar-lesson-${l.id}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        l.id === lessonId ? 'bg-accent text-accent-foreground' : 'bg-muted'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{l.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {l.duration_minutes} {t('course_meta_min', 'min')}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Lesson Quiz — full width, below all lesson content */}
        {quiz && safeArray(quiz.questions).length > 0 && (
          <div ref={quizSectionRef} className="mt-10 pt-10 border-t-[3px] border-accent" data-testid="lesson-quiz-section">
            <h2 className="text-xl font-bold mb-5 flex items-center gap-2">
              📝 {quiz.title || t('lesson_quiz', 'Lesson Quiz')}
              {!quiz.is_graded && <Badge variant="secondary" className="ml-2">Practice</Badge>}
            </h2>

            {isCompleted && !quizResult ? (
              <Card className="border-accent/50">
                <CardContent className="p-6 text-center text-muted-foreground">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                  {t('lesson_quiz_already_done', 'You already completed this quiz for this lesson.')}
                </CardContent>
              </Card>
            ) : (
              <Card className="border-accent/50">
                <CardContent className="p-6">
                  {quizResult && quiz.is_graded && (
                    <div className={`mb-6 p-4 rounded-lg ${quizResult.passed ? 'bg-green-500/10 border border-green-500/30' : 'bg-amber-500/10 border border-amber-500/30'}`}>
                      <p className="font-semibold">{quizResult.passed ? '🎉 ' + t('quiz_passed', 'Congratulations! You passed!') : t('quiz_completed', 'Quiz Completed')}</p>
                      <p className="text-sm text-muted-foreground">Score: {quizResult.score}% ({quizResult.correct_answers}/{quizResult.total_questions} correct) • +{quizResult.points_earned} points</p>
                    </div>
                  )}

                  {quizResult && !quiz.is_graded && (
                    <div className="mb-6 p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                      <p className="font-semibold">✓ {t('quiz_practice_done', 'Practice Complete!')}</p>
                    </div>
                  )}

                  {!quizSubmitted && (() => {
                    const questions = safeArray(quiz.questions);
                    const question = questions[currentQuestion];
                    if (!question) return null;

                    const options = safeArray(question.options);
                    const correctIdx = Number(question.correct_answer);
                    const userAnswer = quizAnswers[currentQuestion];
                    const hasAnswered = userAnswer !== -1;
                    const isUserCorrect = hasAnswered && userAnswer === correctIdx;

                    return (
                      <div>
                        <div className="flex items-center justify-between mb-4 text-sm text-muted-foreground">
                          <span>Question {currentQuestion + 1} of {questions.length}</span>
                          <span>{quizAnswers.filter(a => a !== -1).length} answered</span>
                        </div>
                        <Progress value={((currentQuestion + 1) / questions.length) * 100} className="mb-6" />

                        <div className="border rounded-lg p-4">
                          <p className="font-medium text-lg mb-4">{currentQuestion + 1}. {question.question}</p>
                          <div className="space-y-3">
                            {options.map((option, oIndex) => {
                              const isSelected = userAnswer === oIndex;
                              const isCorrectOption = oIndex === correctIdx;

                              let bgClass = 'hover:bg-muted cursor-pointer';
                              if (hasAnswered) {
                                bgClass = isSelected
                                  ? (isCorrectOption ? 'bg-green-100 border-green-500 dark:bg-green-900/30' : 'bg-red-100 border-red-500 dark:bg-red-900/30')
                                  : 'opacity-60 cursor-not-allowed';
                              }

                              return (
                                <button
                                  key={oIndex}
                                  type="button"
                                  disabled={hasAnswered || quizSubmitted}
                                  onClick={() => {
                                    if (!hasAnswered && !quizSubmitted) {
                                      const newAnswers = [...quizAnswers];
                                      newAnswers[currentQuestion] = oIndex;
                                      setQuizAnswers(newAnswers);
                                    }
                                  }}
                                  className={`w-full text-left p-4 rounded-lg border-2 transition-all ${bgClass}`}
                                >
                                  <span className="flex items-center justify-between">
                                    <span>{option}</span>
                                    {hasAnswered && isSelected && isCorrectOption && <Check className="w-5 h-5 text-green-600" />}
                                    {hasAnswered && isSelected && !isCorrectOption && <span className="text-red-600 font-bold text-lg">✗</span>}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                          {hasAnswered && !isUserCorrect && (
                            <p className="mt-4 text-sm bg-green-50 dark:bg-green-900/20 p-3 rounded text-green-700 dark:text-green-400 font-medium">
                              ✓ Correct answer: {options[correctIdx] || 'N/A'}
                            </p>
                          )}
                        </div>

                        <div className="flex justify-between mt-6">
                          <Button
                            variant="outline"
                            disabled={currentQuestion === 0}
                            onClick={() => setCurrentQuestion(prev => prev - 1)}
                          >
                            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
                          </Button>

                          {currentQuestion < questions.length - 1 ? (
                            <Button
                              disabled={!hasAnswered}
                              onClick={() => setCurrentQuestion(prev => prev + 1)}
                            >
                              Next <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                          ) : (
                            <Button
                              disabled={quizAnswers.some(a => a === -1) || quizSubmitted}
                              onClick={async () => {
                                try {
                                  const res = await axios.post(`${API_URL}/quiz/submit`, {
                                    quiz_id: quiz.id,
                                    answers: quizAnswers
                                  }, { headers });
                                  setQuizResult(res.data);
                                  setQuizSubmitted(true);
                                  toast.success(res.data.passed ? t('quiz_passed', 'You passed!') : t('quiz_done', 'Quiz completed'));
                                  // Finishing the quiz is an explicit user action, same as clicking
                                  // "Mark as Complete" — auto-completes the lesson so the content
                                  // locks immediately instead of requiring a second click. Goes
                                  // through markLessonComplete (not handleMarkComplete) since the
                                  // gate check would otherwise read the pre-submit quizResult.
                                  if (!isCompleted) {
                                    await markLessonComplete();
                                  }
                                } catch (err) {
                                  toast.error(t('quiz_error', 'Failed to submit quiz'));
                                }
                              }}
                            >
                              Submit Quiz
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {quizSubmitted && (
                    <Button
                      variant="outline"
                      className="w-full mt-4"
                      onClick={() => {
                        setQuizAnswers(new Array(safeArray(quiz.questions).length).fill(-1));
                        setQuizSubmitted(false);
                        setQuizResult(null);
                        setCurrentQuestion(0);
                      }}
                    >
                      {t('quiz_retry', 'Try Again')}
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LessonPlayer;
